import { useEffect, useCallback, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useOutletContext } from 'react-router-dom'
import type { EvaluationOutletContext } from '../Evaluations_ShowCN'
import { History } from 'lucide-react'
import AuditTrail from 'components/AuditTrail/AuditTrail'
import {
	fetchAuditLogPage,
	PAGE_SIZE_DEFAULT,
	type AuditFilters,
	type AuditFacets,
} from 'slices/auditLogSlice'
import ActivityFilters from './ActivityFilters'

const EMPTY_FACETS: AuditFacets = { users: [], entityTypes: [], actions: [] }

const EvaluationActivity = () => {
	const { evalId } = useOutletContext<EvaluationOutletContext>()
	const dispatch = useDispatch()
	const page = useSelector((state: any) => state.auditLog?.byEvaluation?.[evalId])

	const entries = page?.entries || []
	const total = page?.total || 0
	const loadedCount = page?.loadedCount || 0
	const isLoading = page?.isLoading || false
	const facets: AuditFacets = page?.facets || EMPTY_FACETS

	// Filters live in component state — committed to the slice via the
	// fetched page's `filters` echo. Default range = last 30 days, matching
	// the J2 acceptance criterion.
	const [filters, setFilters] = useState<AuditFilters>(() => ({
		since: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
	}))

	// Refetch whenever filter signature changes. We serialise the filter
	// shape to a stable key so equivalent filters don't re-trigger.
	const filterKey = useMemo(() => JSON.stringify(filters), [filters])

	useEffect(() => {
		;(dispatch as any)(
			fetchAuditLogPage({
				evalId,
				offset: 0,
				limit: PAGE_SIZE_DEFAULT,
				append: false,
				filters,
			}),
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, evalId, filterKey])

	const handleLoadMore = useCallback(() => {
		;(dispatch as any)(
			fetchAuditLogPage({
				evalId,
				offset: loadedCount,
				limit: PAGE_SIZE_DEFAULT,
				append: true,
				filters,
			}),
		)
	}, [dispatch, evalId, loadedCount, filters])

	return (
		<div className="space-y-5">
			<div>
				<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
					<History className="w-5 h-5 text-[#0080fe]" />
					Activity
				</h3>
				<p className="text-sm text-gray-500">
					Chronological audit trail of changes to this evaluation. Filter by entity type,
					action, user, or date range.
				</p>
			</div>

			<ActivityFilters facets={facets} filters={filters} onChange={setFilters} />

			<AuditTrail
				entries={entries}
				total={total}
				isLoading={isLoading}
				onLoadMore={handleLoadMore}
				emptyText="No activity matches the current filters."
			/>
		</div>
	)
}

export default EvaluationActivity
